import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { SITE } from "./pageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Gallery photos (mirrors HARDCODED_PHOTOS in GalleryPage.js — kept in sync manually).
// We emit ImageObject schema so Google Image Search treats each as a discrete asset
// with proper attribution and a clear caption.
const GALLERY_PHOTOS = [
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/z88nvx3c_IMG_7573.jpeg", caption: "Rocky shoreline at Whale Cove on the Bay of Fundy" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/0zj0zjrb_IMG_7577.jpeg", caption: "Digby Neck coastline, Bay of Fundy — Nova Scotia" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/xkvkomv6_IMG_7542%20%281%29.jpeg", caption: "Misty morning on the Bay of Fundy rocks at Whale Cove" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/fjjgmrzd_IMG_8812.jpeg", caption: "Fog rolling over the Bay of Fundy shoreline at Whale Cove" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/1iyby5ze_IMG_8792.jpeg", caption: "Building the access road into the Whale Cove off-grid sites" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/shg7ofp3_IMG_8836.jpeg", caption: "Moss-covered rocks along the forest trail to the Bay" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/6snqon67_IMG_8834.jpeg", caption: "Rock crevice on the trail down to the Bay of Fundy" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/a4iykfzj_IMG_6060.jpeg", caption: "Where the road meets the forest at Whale Cove" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/sfsi5yb6_IMG_8932.JPG", caption: "New gravel road through the birch — Whale Cove off-grid build" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/fz2wwh16_IMG_8933.JPG", caption: "The access road, built on bedrock — Whale Cove Off Grid" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/0t4mbdem_IMG_8959.jpeg", caption: "Quartz veins in the Bay of Fundy shoreline rock at Whale Cove" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/xxz8bnzh_IMG_8964.jpeg", caption: "Seaweed-covered boulder at low tide, Bay of Fundy" },
];

const WHALE_SPECIES = [
  { name: "Humpback Whale", latin: "Megaptera novaeangliae", caption: "Humpback whale silhouette — Bay of Fundy summer resident" },
  { name: "Fin Whale", latin: "Balaenoptera physalus", caption: "Fin whale silhouette — second-largest animal on Earth, Bay of Fundy" },
  { name: "Minke Whale", latin: "Balaenoptera acutorostrata", caption: "Minke whale silhouette — Bay of Fundy" },
  { name: "North Atlantic Right Whale", latin: "Eubalaena glacialis", caption: "North Atlantic right whale silhouette — critically endangered" },
  { name: "Harbor Porpoise", latin: "Phocoena phocoena", caption: "Harbor porpoise silhouette — Bay of Fundy" },
  { name: "Atlantic White-sided Dolphin", latin: "Lagenorhynchus acutus", caption: "Atlantic white-sided dolphin silhouette — Bay of Fundy" },
];

/**
 * <RichSchemas /> — route-aware structured data emitter.
 *
 * Adds rich result schemas on top of the basic per-page meta tags (which live
 * in /components/seo/SEO.js). These unlock:
 *   - VideoObject → Google "Videos" tab attribution to whalecoveoffgrid.ca
 *   - ImageGallery → richer treatment in Google Image Search
 *   - CreativeWork (whale silhouettes) → image attribution & topical signal
 */
export default function RichSchemas() {
  const { pathname } = useLocation();
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    if (pathname !== "/videos" && pathname !== "/gallery") return;
    // /gallery also embeds videos at the top of the masonry, so we fetch in both cases.
    axios
      .get(`${API}/videos/public`)
      .then((res) => setVideos(res.data.videos || []))
      .catch(() => setVideos([]));
  }, [pathname]);

  const schemas = [];

  // /videos — emit a CollectionPage listing all VideoObjects
  if (pathname === "/videos" && videos.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Videos — Whale Cove Off Grid",
      url: `${SITE.url}/videos`,
      hasPart: videos.map((v) => ({
        "@type": "VideoObject",
        name: v.title || "Whale Cove Off Grid",
        description: v.description || `${v.title} — Whale Cove Off Grid, Bay of Fundy, Nova Scotia.`,
        thumbnailUrl:
          v.custom_thumbnail_url ||
          `https://i.ytimg.com/vi/${v.youtube_id}/maxresdefault.jpg`,
        uploadDate: (v.created_at || "").slice(0, 10) || "2026-01-01",
        contentUrl: `https://www.youtube.com/watch?v=${v.youtube_id}`,
        embedUrl: `https://www.youtube-nocookie.com/embed/${v.youtube_id}`,
        publisher: {
          "@type": "Organization",
          name: SITE.name,
          url: SITE.url,
        },
      })),
    });
  }

  // /gallery — ImageGallery with each photo as an ImageObject, plus inline videos
  if (pathname === "/gallery") {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "ImageGallery",
      name: "Photography — Whale Cove Off Grid on the Bay of Fundy",
      url: `${SITE.url}/gallery`,
      description:
        "An editorial gallery of Whale Cove Off Grid: oceanfront RV site, tides, light, whales, and the wild Atlantic edge of Digby Neck, Nova Scotia.",
      image: GALLERY_PHOTOS.map((p) => ({
        "@type": "ImageObject",
        contentUrl: p.src,
        caption: p.caption,
        creditText: SITE.name,
        copyrightNotice: `© ${new Date().getFullYear()} ${SITE.name}`,
      })),
    });

    // Also surface the videos here for the embedded video pane at the top of the gallery
    if (videos.length > 0) {
      videos.forEach((v) => {
        schemas.push({
          "@context": "https://schema.org",
          "@type": "VideoObject",
          name: v.title || "Whale Cove Off Grid",
          description:
            v.description ||
            `${v.title} — Whale Cove Off Grid, Bay of Fundy, Nova Scotia.`,
          thumbnailUrl:
            v.custom_thumbnail_url ||
            `https://i.ytimg.com/vi/${v.youtube_id}/maxresdefault.jpg`,
          uploadDate: (v.created_at || "").slice(0, 10) || "2026-01-01",
          contentUrl: `https://www.youtube.com/watch?v=${v.youtube_id}`,
          embedUrl: `https://www.youtube-nocookie.com/embed/${v.youtube_id}`,
        });
      });
    }
  }

  // /whales — CreativeWork collection for the species silhouettes
  if (pathname === "/whales") {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Whales of the Bay of Fundy — Whale Cove Off Grid",
      url: `${SITE.url}/whales`,
      about: WHALE_SPECIES.map((w) => ({
        "@type": "Taxon",
        name: w.name,
        alternateName: w.latin,
        description: w.caption,
      })),
    });
  }

  if (schemas.length === 0) return null;

  return (
    <Helmet>
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
